import type { QuestionTrend, SurveyWithinTimeFrame } from '../../api/queries';
import { scoreColourOf, toneOf } from '../../domain/psychological-safety/team-result';

export type ChangeDirection = 'up' | 'down' | 'stable';

/** Endringer mindre enn dette regnes som uendret */
const UNCHANGED_THRESHOLD = 0.005;

export const colorForScore = (score: number) =>
  `var(--ds-color-${toneOf[scoreColourOf(score)]}-base-default)`;

export const changeTones: Record<ChangeDirection, 'success' | 'danger' | 'neutral'> = {
  up: 'success',
  down: 'danger',
  stable: 'neutral',
};

/** Endringen fra første til siste måling i perioden, eller null når det bare er én måling */
export const getPeriodChange = (questionTrend: QuestionTrend) => {
  const trends = questionTrend.trends ?? [];
  if (trends.length < 2) return null;
  const diff = trends[trends.length - 1].score - trends[0].score;
  const direction: ChangeDirection =
    diff > UNCHANGED_THRESHOLD ? 'up' : diff < -UNCHANGED_THRESHOLD ? 'down' : 'stable';
  return { diff, direction };
};

export const getLatestScore = (questionTrend: QuestionTrend) =>
  questionTrend.trends?.[questionTrend.trends.length - 1]?.score;

/** Antall ulike målinger (datoer) i perioden */
export const countSurveys = (questionTrends: QuestionTrend[]) =>
  new Set(questionTrends.flatMap((questionTrend) => questionTrend.trends.map((trend) => trend.date))).size;

const pad = (value: number) => String(value).padStart(2, '0');

/** Brukerens kalenderdato som YYYY-MM-DD (ikke UTC, som toISOString gir) */
export const toLocalISODate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Samme kalenderdato i dag for ett år siden */
export const oneYearAgo = (today: Date = new Date()) =>
  toLocalISODate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()));

/** YYYY-MM-DD fra backend som lokal dato, så datoen ikke flytter seg i tidssoner vest for UTC */
export const parseCalendarDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Punktene i formatet grafene bruker */
export const toChartData = (questionTrend: QuestionTrend): SurveyWithinTimeFrame[] =>
  questionTrend.trends.map((trend) => ({
    title: questionTrend.question.question,
    date: parseCalendarDate(trend.date),
    averageScore: trend.score,
  }));
